import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo } from '@/mocks/about';
import { siteInfo } from '@/mocks/home';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement);
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      return;
    }
    setFormStatus('loading');
    setFormError('');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d95mstd7gtqne2as9l0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    description: aboutInfo.description,
    url: 'https://heiliaochigua.com',
    email: aboutInfo.contact.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
    },
    sameAs: [
      'https://weibo.com',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliaochigua.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://heiliaochigua.com/about' },
    ],
  };

  return (
    <>
      <title>关于我们 - {siteInfo.name} | 品牌介绍 联系方式 商务合作</title>
      <meta name="description" content={`${siteInfo.name} - ${aboutInfo.description.slice(0, 120)}`} />
      <meta name="keywords" content="关于我们,黑料吃瓜,品牌介绍,联系方式,商务合作" />
      <link rel="canonical" href="https://heiliaochigua.com/about" />
      <meta property="og:title" content={`关于我们 - ${siteInfo.name}`} />
      <meta property="og:description" content={aboutInfo.description.slice(0, 160)} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://heiliaochigua.com/about" />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          {/* Breadcrumb */}
          <div className="bg-background-100 py-8 md:py-12">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-foreground-950 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-950 font-medium">关于我们</span>
              </nav>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950">
                关于 <span className="text-primary-500">黑料吃瓜</span>
              </h1>
              <p className="mt-3 text-foreground-500 text-sm max-w-2xl">
                {aboutInfo.slogan}
              </p>
            </div>
          </div>

          {/* Brand Intro */}
          <section className="py-12 md:py-16" aria-label="品牌介绍">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                    品牌故事
                  </h2>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    {aboutInfo.description}
                  </p>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    {aboutInfo.mission}
                  </p>
                  <p className="text-foreground-700 leading-relaxed">
                    {aboutInfo.vision}
                  </p>
                </div>
                <div className="relative rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20editorial%20office%20interior%20with%20warm%20ambient%20lighting%2C%20wooden%20desk%20with%20laptop%20and%20documents%2C%20creative%20workspace%20atmosphere%2C%20clean%20aesthetic%2C%20soft%20natural%20light%20through%20window%2C%20peaceful%20and%20professional%20environment%2C%20high%20detail%20architectural%20photography&width=900&height=675&seq=about-hero-2026&orientation=landscape"
                    alt={`${siteInfo.name} 编辑部办公环境`}
                    className="w-full h-full object-cover"
                    title={`${siteInfo.name} - 品牌形象`}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Platform Stats */}
          <section className="bg-background-100 py-12 md:py-16" aria-label="平台数据">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
                平台数据
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
                {aboutInfo.stats.map((stat, idx) => (
                  <div key={idx} className="bg-background-50 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-12 md:py-16" aria-label="平台特色">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
                为什么选择我们
              </h2>
              <p className="text-foreground-500 text-sm text-center mb-10 max-w-xl mx-auto">
                四大核心优势，为您带来最优质的吃瓜体验
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {aboutInfo.features.map((feature, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg p-6 text-center group hover:-translate-y-1 transition-all duration-300">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${feature.icon} text-2xl`}></i>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="bg-background-100 py-12 md:py-16" aria-label="用户口碑">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-sm text-center mb-10">
                来自真实用户的评价
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {aboutInfo.reviews.map((review, idx) => (
                  <div key={idx} className="bg-background-50 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div>
                        <div className="text-sm font-semibold text-foreground-950">{review.name}</div>
                        <div className="text-xs text-foreground-400">{review.date}</div>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                          <i className="ri-star-fill text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {review.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 md:py-16" aria-label="常见问题">
            <div className="px-4 md:px-6 max-w-3xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm text-center mb-10">
                关于我们的常见疑问解答
              </p>
              <div className="space-y-3">
                {aboutInfo.faq.map((item, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    >
                      <span className="text-foreground-950 font-medium text-sm pr-4">
                        {item.question}
                      </span>
                      <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </span>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaq === idx ? 'max-h-96 pb-5 px-5' : 'max-h-0'
                      }`}
                    >
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Business Cooperation & Contact */}
          <section className="bg-background-100 py-12 md:py-16" aria-label="商务合作与联系方式">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                    商务合作
                  </h2>
                  <p className="text-foreground-500 text-sm mb-8">
                    欢迎品牌方、广告主、内容平台与我们洽谈合作
                  </p>
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-readdy-form=""
                    id="business-form"
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 / 公司名称 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的姓名或公司名称"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱地址 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        合作意向
                      </label>
                      <select
                        id="company"
                        name="company"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors cursor-pointer"
                      >
                        <option value="">请选择合作类型</option>
                        <option value="广告投放">广告投放</option>
                        <option value="内容合作">内容合作</option>
                        <option value="品牌推广">品牌推广</option>
                        <option value="其他合作">其他合作</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        合作说明 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                        placeholder="请简要描述合作需求（500字以内）"
                      />
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="whitespace-nowrap w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交合作意向'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">提交成功！我们会尽快与您联系。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError || '提交失败，请稍后重试'}</p>
                    )}
                  </form>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                    联系方式
                  </h2>
                  <p className="text-foreground-500 text-sm mb-8">
                    多种渠道，随时与我们取得联系
                  </p>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4 p-4 bg-background-50 rounded-lg">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                        <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                          {aboutInfo.contact.email}
                        </a>
                        <p className="text-xs text-foreground-400 mt-0.5">新闻线索和爆料投稿</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-background-50 rounded-lg">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-briefcase-line text-lg"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-950 mb-1">商务合作邮箱</h3>
                        <a href={`mailto:${aboutInfo.contact.businessEmail}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                          {aboutInfo.contact.businessEmail}
                        </a>
                        <p className="text-xs text-foreground-400 mt-0.5">广告投放与商业合作洽谈</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-background-50 rounded-lg">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-weibo-line text-lg"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-950 mb-1">官方微博</h3>
                        <a href="#" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" rel="nofollow">
                          {aboutInfo.contact.weibo}
                        </a>
                        <p className="text-xs text-foreground-400 mt-0.5">关注获取最新爆料推送</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-background-50 rounded-lg">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-950 mb-1">办公地址</h3>
                        <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-12 md:py-16">
            <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                开始你的吃瓜之旅
              </h2>
              <p className="text-foreground-500 text-sm mb-6">
                每天都有新鲜爆料，别再错过任何一个大瓜
              </p>
              <Link
                to="/"
                className="inline-block whitespace-nowrap px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                立即返回首页吃瓜
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}